import type { ReactNode } from 'react';
import { AlertCircle, CheckCircle2, Info, Loader2, TriangleAlert, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useUiStore } from '@/store/ui.store';

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn('h-4 w-4 animate-spin text-brand-500', className)} />;
}

export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn('animate-pulse rounded bg-surface-sunken dark:bg-night-sunken', className)}
      aria-hidden
    />
  );
}

export function TableSkeleton({ rows = 6, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div className="space-y-2 p-4">
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex gap-3">
          {Array.from({ length: columns }).map((_, colIndex) => (
            <Skeleton key={colIndex} className={cn('h-5 flex-1', colIndex === 0 && 'max-w-[40%]')} />
          ))}
        </div>
      ))}
    </div>
  );
}

interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
}

export function EmptyState({ icon, title, description, action, className }: EmptyStateProps) {
  return (
    <div className={cn('flex flex-col items-center justify-center px-6 py-14 text-center', className)}>
      {icon && (
        <div className="mb-3 rounded-full bg-surface-sunken p-3 text-ink-subtle dark:bg-night-sunken dark:text-slate-500">
          {icon}
        </div>
      )}
      <p className="text-sm font-medium text-ink dark:text-slate-100">{title}</p>
      {description && (
        <p className="mt-1 max-w-sm text-sm text-ink-muted dark:text-slate-400">{description}</p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <EmptyState
      icon={<AlertCircle className="h-5 w-5 text-negative" />}
      title="Could not load this"
      description={message}
      action={
        onRetry && (
          <button
            onClick={onRetry}
            className="rounded-lg bg-surface-sunken px-3 py-1.5 text-xs font-medium text-ink hover:bg-surface-border dark:bg-night-sunken dark:text-slate-100"
          >
            Try again
          </button>
        )
      }
    />
  );
}

const TOAST_ICONS = {
  success: <CheckCircle2 className="h-4 w-4 text-positive" />,
  error: <AlertCircle className="h-4 w-4 text-negative" />,
  warning: <TriangleAlert className="h-4 w-4 text-warning" />,
  info: <Info className="h-4 w-4 text-brand-500" />,
};

export function Toaster() {
  const toasts = useUiStore((s) => s.toasts);
  const dismiss = useUiStore((s) => s.dismissToast);

  if (toasts.length === 0) return null;

  return (
    <div
      className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-full max-w-sm flex-col gap-2"
      role="status"
      aria-live="polite"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className="pointer-events-auto flex items-start gap-3 rounded-xl border border-surface-border bg-white p-3 shadow-raised animate-slide-in dark:border-night-border dark:bg-night-raised"
        >
          <div className="mt-0.5">{TOAST_ICONS[toast.variant]}</div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-ink dark:text-slate-100">{toast.title}</p>
            {toast.description && (
              <p className="mt-0.5 break-words text-xs text-ink-muted dark:text-slate-400">
                {toast.description}
              </p>
            )}
          </div>
          <button
            onClick={() => dismiss(toast.id)}
            aria-label="Dismiss"
            className="rounded p-0.5 text-ink-subtle hover:text-ink dark:hover:text-slate-200"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
}
